/**
 * Pure decision logic for the online-recovery probe (see `useOnlineRecovery`).
 *
 * Background: the live-update loop depends on react-query polling, which pauses
 * all queries while `onlineManager` reports offline. On low-RAM mobile the OS
 * freezes the web process when backgrounded; on resume the browser sometimes
 * never delivers the `online` event, so `onlineManager` stays stuck offline and
 * every query remains `paused` — auto-update is dead until a manual reload.
 * (Confirmed by a field report: on resume `rqOnline:false`, `paused:8`,
 * `fetching:0` while NetInfo reported a healthy 4g/10Mbps link.)
 */

/** Lifecycle events after which connectivity should be re-checked. */
export const ONLINE_RECHECK_EVENTS = [
  'visibilitychange',
  'resume',
  'pageshow',
  'online',
] as const

/**
 * Whether to run a connectivity probe in response to a lifecycle event.
 *
 * Probe only when react-query currently believes it is OFFLINE (its queries are
 * paused → auto-update is stuck) and the page has just regained visibility
 * after a resume — or the browser fired its own `online` event (which can
 * arrive late, or not at all, after a freeze). Gating on "offline" keeps this a
 * no-op on every healthy resume.
 */
export const shouldProbeForOnline = (
  eventType: string,
  visibilityState: string,
  reactQueryOnline: boolean
): boolean =>
  !reactQueryOnline &&
  (ONLINE_RECHECK_EVENTS as readonly string[]).includes(eventType) &&
  (eventType === 'online' || visibilityState === 'visible')

/**
 * Whether to probe when react-query's onlineManager transitions to offline.
 *
 * The lifecycle-event probe (`shouldProbeForOnline`) can miss the real case:
 * the resume/visibilitychange events often fire while onlineManager still
 * reports online, and it only flips to offline a few hundred ms later — by
 * which point no probe-triggering event fires. Reacting to the offline
 * transition itself, while the page is visible, closes that race. We act only
 * on the offline edge (`online === false`); the probe re-confirms connectivity
 * and only forces online back when the server is actually reachable.
 */
export const shouldProbeOnOfflineTransition = (
  online: boolean,
  visibilityState: string
): boolean => !online && visibilityState === 'visible'

/**
 * Whether a periodic watchdog tick should (re-)arm a recovery session.
 *
 * The event-driven arms (`shouldProbeForOnline`, `shouldProbeOnOfflineTransition`)
 * each fire once. After a session exhausts its wall-clock budget while the page
 * is STILL visible and STILL offline — or when the page resumed already-offline
 * (onlineManager flipped offline while hidden, so no fresh transition fires) and
 * no further lifecycle event arrives — nothing re-probes. A user staring at a
 * frozen calendar generates no events, so the stuck-offline state persists
 * indefinitely (the field reports show minutes of monotonically-growing visit
 * staleness). A time-driven watchdog closes that hole: while visible and offline
 * it keeps re-arming, so the first moment the server is reachable, polling
 * resumes. It is a no-op while online (nothing to fix) and while hidden (no
 * background probing), so it never competes with the app on a healthy session.
 */
export const shouldArmWatchdogProbe = (
  visibilityState: string,
  reactQueryOnline: boolean
): boolean => !reactQueryOnline && visibilityState === 'visible'

export type RecoveryAction = 'noop' | 'force-online' | 'probe'

/**
 * What to do once a lifecycle/transition/watchdog arm has decided react-query
 * MIGHT need recovering (see `useOnlineRecovery`). This is the fast path that
 * fixes the dominant Mode-A field signature.
 *
 * Background: the probe loop deliberately distrusts `navigator.onLine` because
 * it can lag wrongly-false around freeze/resume. But every confirmed stuck case
 * in production (`recoveryFailed` reports) has the OPPOSITE polarity —
 * `navigator.onLine === true` while `onlineManager` is wrongly offline and all
 * queries are paused. Trusting `navigator.onLine === true` is therefore both
 * safe and the right direction: it unpauses immediately (`force-online`) so the
 * visit self-heal refetch actually runs, instead of leaving the user stuck for
 * the 3-20s a backend probe round takes. Only when the browser ALSO reports
 * offline do we fall back to the async `probe` (the wrongly-false case the probe
 * was built for); when react-query already thinks it is online there is nothing
 * to do (`noop`).
 */
export const recoveryActionFor = (
  reactQueryOnline: boolean,
  navigatorOnLine: boolean
): RecoveryAction => {
  if (reactQueryOnline) {
    return 'noop'
  }
  return navigatorOnLine ? 'force-online' : 'probe'
}

/** Minimal slice of react-query's `onlineManager` the recovery action needs. */
export type OnlineController = {
  isOnline: () => boolean
  setOnline: (online: boolean) => void
}

/**
 * Imperative recovery step: read the current online/navigator state, decide via
 * `recoveryActionFor`, and act. `force-online` flips `onlineManager` online now
 * (the fast path) and calls `onForce`; `probe` defers to the async backend probe
 * via `onProbe`; `noop` does nothing. Returns the action taken. Kept here (not
 * inline in the hook) and dependency-injected so the force-vs-probe dispatch —
 * the heart of the Mode-A fix — is unit-testable without the DOM/global manager.
 */
export const applyOnlineRecovery = (
  online: OnlineController,
  navigatorOnLine: boolean,
  handlers: { onForce?: () => void; onProbe?: () => void }
): RecoveryAction => {
  const action = recoveryActionFor(online.isOnline(), navigatorOnLine)
  if (action === 'force-online') {
    online.setOnline(true)
    handlers.onForce?.()
  } else if (action === 'probe') {
    handlers.onProbe?.()
  }
  return action
}

export type RecoveryTickAction =
  | 'stop-online'
  | 'stop-hidden'
  | 'stop-exhausted'
  | 'probe'

/**
 * One step of the recovery retry loop (see `useOnlineRecovery`).
 *
 * Earlier fixes each read a point-in-time signal (`navigator.onLine`, the
 * `online` event, the offline transition) at a single lifecycle-event instant —
 * and every one of those signals lags or is dropped around freeze/resume, so a
 * new timing variant kept slipping through. This loop stops trusting any
 * point-in-time flag: while the page is visible and react-query is offline it
 * just keeps probing the network until a probe succeeds (the probe reaches the
 * server even when `navigator.onLine` is wrongly false). A lagging flag, a
 * dropped `online` event, or a brief genuine-offline-at-resume all self-heal on
 * the next tick; genuinely offline simply keeps failing (no force-online) until
 * the session deadline, after which the next lifecycle event re-arms.
 *
 * The cap is wall-clock `elapsedMs >= maxDurationMs` rather than an attempt
 * count, so the bound holds regardless of how long each probe takes (a probe
 * can run up to its abort timeout when offline). One in-flight probe may finish
 * just past the deadline; the next tick then stops.
 */
export const recoveryTick = (
  reactQueryOnline: boolean,
  visible: boolean,
  elapsedMs: number,
  maxDurationMs: number
): RecoveryTickAction => {
  if (reactQueryOnline) {
    return 'stop-online'
  }
  if (!visible) {
    return 'stop-hidden'
  }
  if (elapsedMs >= maxDurationMs) {
    return 'stop-exhausted'
  }
  return 'probe'
}
